import React from 'react';
import {
  Box,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { PAGE_HEADER_TITLE_SX } from '../constants/page-header';
import PageTitleIcon from './PageTitleIcon';
import { APP_SHAPE } from '../constants/shape';

export interface PageHeaderProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  onBack?: () => void;
  backLabel?: string;
  titleSx?: SxProps<Theme>;
  titleComponent?: React.ElementType;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Page header — icon badge, title, optional subtitle, and trailing actions.
 * Matches the Zero platform look (elevated Paper card).
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  icon,
  title,
  subtitle,
  actions,
  onBack,
  backLabel,
  titleSx,
  titleComponent = 'h1',
  children,
  sx,
}) => {
  const theme = useTheme();

  const backButton = onBack ? (
    <IconButton
      onClick={onBack}
      aria-label={backLabel ?? 'Back'}
      size="small"
      sx={{
        flexShrink: 0,
        color: 'text.secondary',
        '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
      }}
    >
      <ArrowBackIcon fontSize="small" />
    </IconButton>
  ) : null;

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 3,
        p: 3,
        borderRadius: `${APP_SHAPE.radiusLg}px`,
        bgcolor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        gap: children ? 2 : 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          {backLabel && backButton ? (
            <Tooltip title={backLabel}>{backButton}</Tooltip>
          ) : (
            backButton
          )}
          {icon ? <PageTitleIcon icon={icon} /> : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              component={titleComponent}
              sx={{ ...PAGE_HEADER_TITLE_SX, m: 0, ...titleSx }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
        {actions ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
      {children}
    </Paper>
  );
};

export default PageHeader;
